import { useMemo, useState } from 'react'
import { useAuth } from '../../hooks/useAuth'
import { useAdmin } from '../../hooks/useAdmin'
import { parseSpendBudget, parseSpendPoLines, parseSpendWorkbook, replaceCompanySpend } from '../../utils/spendCubeImport'

export default function SpendCubeImportPanel() {
  const { effectiveCompanyId } = useAuth()
  const { companies } = useAdmin()
  const [companyId, setCompanyId] = useState(effectiveCompanyId || '')
  const [fileName, setFileName] = useState('')
  const [rowCount, setRowCount] = useState<number | null>(null)
  const [rows, setRows] = useState<Awaited<ReturnType<typeof parseSpendWorkbook>>['rows'] | null>(null)
  const [budget, setBudget] = useState<ReturnType<typeof parseSpendBudget>>(null)
  const [poLines, setPoLines] = useState<ReturnType<typeof parseSpendPoLines>>(null)
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const selected = useMemo(
    () => companies.find((c) => c.id === companyId) || null,
    [companies, companyId]
  )

  const onFile = async (file: File | null) => {
    setMessage(null)
    setError(null)
    setRows(null)
    setBudget(null)
    setPoLines(null)
    setRowCount(null)
    setFileName(file?.name || '')
    if (!file) return
    try {
      const buffer = await file.arrayBuffer()
      const parsed = parseSpendWorkbook(buffer)
      setRows(parsed.rows)
      setBudget(parseSpendBudget(buffer))
      setPoLines(parseSpendPoLines(buffer))
      setRowCount(parsed.rows.length)
      setMessage(`Read ${parsed.rows.length.toLocaleString()} lines from “${parsed.sheetName}”.`)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not read that workbook.')
    }
  }

  const onReplace = async () => {
    if (!companyId || !rows) return
    const name = selected?.name || 'this company'
    if (!window.confirm(`Replace spend lines for ${name}? The supplier name map is kept.`)) return
    setBusy(true)
    setError(null)
    setMessage(null)
    try {
      const summary = await replaceCompanySpend(companyId, rows, budget, poLines)
      const parts = [
        `Loaded ${summary.lines.toLocaleString()} lines for ${name}.`,
        `${summary.mapped.toLocaleString()} linked to a supplier, ${summary.unmapped.toLocaleString()} not linked.`,
        `Name map kept ${summary.mapRowsKept.toLocaleString()} existing row${summary.mapRowsKept === 1 ? '' : 's'}.`,
        summary.budgetQuarters
          ? `Loaded ${summary.budgetQuarters} budget quarter${summary.budgetQuarters === 1 ? '' : 's'}.`
          : '',
        summary.poLines
          ? `Loaded ${summary.poLines.toLocaleString()} PO commitment line${summary.poLines === 1 ? '' : 's'}.`
          : '',
      ]
      if (summary.errors.length) parts.push(summary.errors.join(' '))
      setMessage(parts.filter(Boolean).join(' '))
      if (summary.errors.length && summary.lines === 0) setError(summary.errors.join(' '))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Import failed.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="p-4 sm:p-6 space-y-4 max-w-3xl">
      <p className="text-sm text-gray-600">
        Upload the Raw Data sheet for one company. Lines for that company are replaced. The parent-name map is kept,
        then new names are matched to suppliers that company can see.
      </p>
      <label className="block text-sm font-medium text-gray-700">
        Company
        <select
          className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
          value={companyId}
          onChange={(e) => setCompanyId(e.target.value)}
        >
          <option value="">Select a company</option>
          {companies.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
      </label>
      <label className="block text-sm font-medium text-gray-700">
        Workbook (.xlsx)
        <input
          type="file"
          accept=".xlsx,.xls"
          className="mt-1 block w-full text-sm"
          onChange={(e) => void onFile(e.target.files?.[0] || null)}
        />
      </label>
      {fileName && rowCount != null && (
        <p className="text-sm text-gray-600">
          {fileName}: {rowCount.toLocaleString()} lines ready.
        </p>
      )}
      <button
        type="button"
        disabled={!companyId || !rows || busy}
        onClick={() => void onReplace()}
        className="px-4 py-2 bg-black text-white rounded-md text-sm font-medium disabled:opacity-50"
      >
        {busy ? 'Replacing lines…' : 'Replace this company’s spend'}
      </button>
      {message && <p className="text-sm text-gray-800 whitespace-pre-wrap">{message}</p>}
      {error && <p className="text-sm text-red-700">{error}</p>}
    </div>
  )
}
